import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { ErrorDisplay } from './ErrorDisplay';
import { createAppError, type AppError } from '@/types/error.types';

interface Props {
  children: ReactNode;
  /** Fallback UI to show on error */
  fallback?: (error: AppError, reset: () => void) => ReactNode;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  error: AppError | null;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if error has pre-attached context (for demo/test errors)
    const hasPreAttachedContext = error && typeof error === 'object' && 'appErrorContext' in error;
    
    // Create structured error from caught error
    // Only provide default context if no pre-attached context exists
    const appError = createAppError(error, hasPreAttachedContext ? undefined : {
      message: 'A critical error occurred in the application',
      severity: 'critical',
      category: 'system',
      recoverable: true,
      suggestedActions: [
        'Try refreshing the page',
        'Clear your browser cache',
        'Contact support if the issue persists',
      ],
    });

    return { error: appError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Call optional error callback
    this.props.onError?.(error, errorInfo);

    // Update error with component stack
    if (this.state.error) {
      this.state.error.technicalDetails = {
        ...this.state.error.technicalDetails!,
        stackTrace: errorInfo.componentStack || error.stack,
      };
    }

    // TODO: Send to error logging service (e.g., Sentry, LogRocket)
    // logErrorToService(error, errorInfo);
  }

  resetError = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // Default error display with blurred background
      return (
        <>
          {/* Blurred background content */}
          <div className="blur-sm pointer-events-none">
            {this.props.children}
          </div>
          {/* Error overlay */}
          <ErrorDisplay
            error={this.state.error}
            recovery={{
              canReload: true,
              canRetry: true,
              onRetry: this.resetError,
            }}
            onClose={() => window.location.reload()}
          />
        </>
      );
    }

    return this.props.children;
  }
}
